import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../App.css';
import { handleApiError } from '../../utils/errorHandler';
import api from '../../utils/api.js';
import PromptSelectModal from '../../components/PromptSelectModal';
import ConfirmModal from '../../components/ConfirmModal';
import FileUploadSection from '../../components/FileUploadSection.js';
import UserSelect from '../../components/UserSelect.js';
import useNavigateBack from '../../utils/useNavigateBack.js';
import { useAIProcessing } from '../../hooks/useAIProcessing.js';
import SkeletonLoader from '../../components/SkeletonLoader.js';
import { formatToJST } from '../../utils/dateFormatter.js';
import CopyButton from '../../components/CopyButton.js';
import { useActivityDiarization } from './diarization/useActivityDiarization.js';
import DiarizationPanel, { diarizationErrorMessage } from './diarization/DiarizationPanel.js';
import DiarizationConfirmNotice from './diarization/DiarizationConfirmNotice.js';
import SpeakerIcon from './diarization/SpeakerIcon.js';
import { DIARIZATION_PHASE } from './diarization/diarizationAdapter.js';
import './diarization/Diarization.css';
import {
  AI_CONTENT_OVERWRITE_NOTICE,
  AI_CONTENT_OUTDATED_DISABLED_REASON,
  AIContentOutdatedNotice,
  hasAnyAIContent,
  isAIContentOutdated,
} from './aiContentOutdated.js';

// 面談音声として受け付ける形式 (SCR-D-001 §5.2 No.2 の注記。#889 E02)。サーバの ALLOWED_AUDIO_MIME_TYPES
// (audio/mpeg・audio/wav・audio/ogg・audio/x-m4a・audio/mp4・audio/flac) に揃え、拡張子を併記する
// (同じ m4a でも OS・ブラウザによって MIME が変わりうるため)。サーバの一覧に無い audio/mp3 は外した
const AUDIO_ACCEPT =
  'audio/mpeg,audio/wav,audio/ogg,audio/x-m4a,audio/mp4,audio/flac,.mp3,.wav,.ogg,.m4a,.mp4,.flac';

// 話者分離の処理中に音声の差し替えを止めるときの文言 (SCR-D-001 §6.4「処理中に止めないもの」、TU-D-024)
const AUDIO_REPLACE_BLOCKED_MESSAGE = '話者分離の処理中は音声を差し替えられません。';

// 音声の取扱の明示 (SCR-D-001 DP-D-008・§8.1 No.4。文言は設計書から写す)。
// 保持期間で音声が消えた後の説明を兼ねるため、音声の有無に依らず同じ文言を出す (DP-D-008。#1923・#2332)。
const AUDIO_HANDLING_NOTICE = '音声はアップロードから7日以上たつと自動で削除され、再生と音声からの文字起こしはできなくなります。文字起こし・要約は残ります。';

// タブのラベルの横の印の、読み上げ用の文言 (SCR-D-001 §6.4「タブの外での表示」)
const TAB_MARK_PENDING_LABEL = '話者分離を処理中';
// 話者分離のエラーの印 (SCR-D-001 §6.3・§6.4「タブの外での表示」。#1468・#889・#1643)。
// 印は通信の失敗（受付が済んでいる場合がある）でも出るため、「開始できませんでした」と断定しない。
// 何が起きたかは文字起こしタブの中の文で伝える
const TAB_MARK_ERROR_LABEL = '話者分離にエラーがあります';
// 話者分離のエラーを読み上げでも知らせる文 (SCR-D-001 §7.2 のスクリーンリーダーの行・§6.3 ③。#1468・#1643)。
// 印だけでは、スクリーンリーダーの利用者はタブに移るまで気づけない（移るきっかけが無い）
const ERROR_LIVE_MESSAGE = '話者分離にエラーがあります。「文字起こし」タブで確認できます。';

// プロンプトモーダル表示設定のマップ化（リファクタ対象③）
const PROMPT_MODAL_CONFIG = {
  upload_summary: { type: 'summary', title: 'AI要約のプロンプトを選択', confirmBtn: '次へ' },
  upload_feedback: { type: 'feedback', title: 'AIフィードバックのプロンプトを選択', confirmBtn: '実行' },
  feedback: { type: 'feedback', title: 'AIフィードバックを実行', confirmBtn: '実行' },
  summary: { type: 'summary', title: 'AI要約を実行', confirmBtn: '実行' },
};

// 進捗ステータスに対応する日本語メッセージ
const STATUS_MESSAGES = {
  transcribing: '文字起こし中...',
  summarizing: '要約を作成中...',
  analyzing: 'AI分析中（要約・フィードバック）...',
  generating_feedback: 'フィードバック生成中...',
};

function ActivityDetailContent({ meetingId }) {
  const navigate = useNavigate();

  const [meetingData, setMeetingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // --- 編集モード用State ---
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);

  // --- タブ用State ---
  const [activeTab, setActiveTab] = useState('basic');

  // --- AI処理用カスタムフック（状態とロジックをカプセル化） ---
  const {
    processingStatus,
    processingError, setProcessingError,
    audioAttachment, setAudioAttachment,
    showConfirmModal, setShowConfirmModal,
    showSummaryConfirmModal,
    showFeedbackConfirmModal,
    showPromptSelectModal,
    promptSelectContext,
    
    handleConfirmAIProcessing,
    handleCancelAIProcessing,
    handleClickGenerateSummary,
    handleConfirmOverwriteSummary,
    handleCancelOverwriteSummary,
    handleClickGenerateFeedback,
    handleConfirmOverwriteFeedback,
    handleCancelOverwriteFeedback,
    handlePromptSelectConfirm,
    handleClosePromptSelectModal
  } = useAIProcessing({ meetingId, meetingData, setMeetingData, isEditing });

  // --- 話者分離（SCR-D-001。既存の processingStatus とは別に持つ。§6.4） ---
  const diar = useActivityDiarization({ meetingId });
  const { loadQuota: loadDiarizationQuota } = diar;
  const isDiarizationPending = diar.enabled && diar.diarization.phase === DIARIZATION_PHASE.pending;
  // 話者分離のエラー（受付の失敗と、受付の後の失敗）。アップロードの後の流れでは利用者は「基本情報」タブを見ており、
  // 文字起こしタブの中のエラーだけでは気づけないため、タブの外にも印を出す (§6.3・§6.4。#1468)。
  // 判定はタブの中のエラーの文と同じ関数で行う（画面を読み直した後の失敗にも印を出す。#1643）。
  // 編集中も出す。pending の間は処理中の印だけを出す（2 つを同時に出さない）
  const hasDiarizationError =
    diar.enabled && !isDiarizationPending && diarizationErrorMessage(diar.diarization) !== null;
  // タブの中のエラーの文（role="alert"）が描かれているか。描かれていなければ、タブの外の通知の領域で知らせる
  // （二重に読み上げない。編集中は文字起こしタブを開いていても描かれない。#1643 comment 6394270427）。
  // 知らせるのは、この画面を開いている間に起きたエラーだけ（開いたときに既にある失敗は知らせない。§7.2）
  const isDiarizationAlertShown = hasDiarizationError && activeTab === 'transcript' && !isEditing;
  const announceDiarizationError = hasDiarizationError && !isDiarizationAlertShown && diar.isErrorNew;

  // 確認モーダルを「文字起こしを実行」(BTN-D-004) から開いたか。アップロードの直後とは 1 行目の文言が違う (§6.2。#1952)
  const [confirmFromRestart, setConfirmFromRestart] = useState(false);
  // 音声を差し替えた後で、文字起こし・要約・FB が今の音声より前に作られたものか (SCR-D-001 §6.2「音声を差し替えた後の内容」。#1952)
  const aiContentOutdated = isAIContentOutdated({ meetingData, audioAttachment });
  // 3 つのタブの注記は AI 処理が動いていない間だけ出す。処理中は「作り直しています」と食い違うため（DESIGN の案。#1952 comment 6580176745）
  const showOutdatedNotice = aiContentOutdated && processingStatus === 'idle';
  // 確認モーダルでキャンセルすると、音声を上げ直すまで文字起こしを始める手段が無かったため、
  // 5 つをすべて満たすときだけ確認モーダルを開き直すボタンを出す (SCR-D-001 §4.3 BTN-D-004。#1952)。
  // ② は「本文が空」に加え、本文が今の音声より前のものであるときも満たす（差し替えの後に作り直せるように）
  const canRestartTranscript = Boolean(audioAttachment)
    && (!meetingData?.meeting_transcript || aiContentOutdated)
    && processingStatus === 'idle'
    && !isDiarizationPending
    && !isEditing;
  const hasGeneratedAnalysis = Boolean(meetingData?.meeting_summary || meetingData?.ai_feedback);
  // 上書きの文を出すか。ボタンから開いたときは印で、差し替えの直後は面談に内容があるか（サーバが印を立てる条件と同じ）で決める。
  // 直後は面談の取り直しの成否に依らず出すため、取り直した印ではなく内容で見る
  const showOverwriteNotice = confirmFromRestart ? aiContentOutdated : hasAnyAIContent(meetingData);
  const openConfirmForRestart = () => {
    setConfirmFromRestart(true);
    setShowConfirmModal(true);
  };

  // 確認モーダルを開いたときに、AI ポイントの残量を取り直す (§5.2.1「取り直す時機」①)
  useEffect(() => {
    if (showConfirmModal) loadDiarizationQuota();
  }, [showConfirmModal, loadDiarizationQuota]);


  // ... ( 
  // データ取得
  // 読み込み中の表示（スケルトン）は、この面談を初めて読み込むときだけ出す (SCR-D-001 §6.1。Issue #2392)。
  // AI 処理の状態が変わるたびに面談を取り直す（下の useEffect の依存の processingStatus。#1495 のフィードバックの
  // 表示がこれに依る）が、その取り直しで画面全体をスケルトンに替えると、結果が出る直前に一瞬画面が消える。
  // 別の面談に移るときは key={meetingId} で部品ごと作り直されるため、この ref も初めからになる。
  const hasLoadedMeetingRef = useRef(false);
  const fetchMeetingData = useCallback(async () => {
    if (!hasLoadedMeetingRef.current) {
      setLoading(true);
    }
    try {
      const response = await api.fetch(`/meeting_detail/${meetingId}`);
      if (!response) return; // Redirected
      if (!response.ok) throw new Error('案件データの取得に失敗しました。');
      const data = await response.json();
      setMeetingData(data);
      hasLoadedMeetingRef.current = true;
      setLoading(false);
    } catch (err) {
      // 取り直しの失敗では前の表示を残し、画面全体をエラーにしない (SCR-D-001 §6.1。#2392 で丹羽様のご判断。
      // 音声の確定の後の取り直し〈reloadMeetingAfterAudioConfirmed。#1952〉と同じ扱い)。再読み込みで正しい表示に戻る
      if (!hasLoadedMeetingRef.current) setError(err.message);
      setLoading(false);
    }
  }, [meetingId]);

  // 画面を開いたときと、AI 処理の状態が変わるたびに面談を取り直す。AI 分析が 400 で返っても、サーバが保存した
  // フィードバックをこの取り直しで出す (SCR-D-001 §6.3。#1495)。依存から processingStatus を外さないこと
  useEffect(() => {
    fetchMeetingData();
  }, [fetchMeetingData, processingStatus]);

  // 音声の確定の後に面談を取り直す（サーバが印 ai_content_outdated を立てるが、確定の応答には入らないため。#1952）。
  // 読み込み中の表示にしない（アップロードの部品を作り直さないため）。失敗しても前の値を残し、エラーも出さない
  // （確定は済んでおり、再読み込みで正しい表示に戻る）
  const reloadMeetingAfterAudioConfirmed = useCallback(async () => {
    try {
      const response = await api.fetch(`/meeting_detail/${meetingId}`);
      if (response?.ok) setMeetingData(await response.json());
    } catch {
      // 前の値を残す
    }
  }, [meetingId]);

  // 戻る
  const handleBackClick = useNavigateBack(
    meetingData?.opportunity_id
      ? `/opportunity/${meetingData.opportunity_id}?tab=activities`
      : '/'
  );

  // ダブルクリック編集
  const handleEditDoubleClick = (field) => {
    if (field === 'audio') return;
    setIsEditing(true);
    setEditData({ ...meetingData });
    setEditError(null);
  };

  // 編集入力
  const handleEditChange = (e, field) => {
    setEditData(prev => ({ ...prev, [field]: e.target.value }));
  };

  // 編集内容を保存
  const handleSaveEdit = async () => {
    setEditLoading(true);
    setEditError(null);
    try {
      const updatePayload = {
        name: editData.name,
        owner_id: editData.owner_id,
        meeting_date: editData.meeting_date,
        meeting_transcript: editData.meeting_transcript,
        meeting_summary: editData.meeting_summary,
        memo: editData.memo,
      };

      const response = await api.fetch(`/meetings/${meetingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      });
      if (!response) return; // Redirected
      if (!response.ok) {
        const errorMessage = await handleApiError(response, '更新に失敗しました。');
        throw new Error(errorMessage);
      }
      const updatedMeeting = await response.json();
      setMeetingData(updatedMeeting);
      setIsEditing(false);
      // 貼り付けた文字起こしを保存すると、サーバが話者分離の結果を作ることがある (SCR-D-001 §6.5「保存の後」。#953)。
      // 作る条件は画面に持たず、保存が成功したら状態を取り直して done かどうかだけを見る。
      // 待たずに取り直す（await しない）: 取り直しの成否を保存の成否に含めないため
      if (diar.enabled) diar.diarization.reload();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  // 編集をキャンセル
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditError(null);
  };

  const handleDelete = async () => {
    setEditLoading(true);
    try {
      const response = await api.fetch(`/meetings/${meetingId}`, {
        method: 'DELETE',
      });
      if (!response) return; // Redirected
      if (!response.ok) {
        throw new Error('削除に失敗しました。');
      }
      setShowDeleteConfirm(false);
      alert('活動を削除しました。');
      // 親の案件詳細画面に戻る
      if (meetingData.opportunity_id) {
        navigate(`/opportunity/${meetingData.opportunity_id}`);
      } else {
        navigate('/');
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setEditLoading(false);
    }
  };


  // (AI関連の各種ハンドラーは useAIProcessing フック内に移動しました)




  // ---------------------------------------------
  // タブコンテンツのレンダリング
  // ---------------------------------------------
  const renderTabContent = () => {
    // ... (case 'basic' の return までは変更なし) ...

    switch (activeTab) {
      case 'basic':
        return (
          <div className="detail-single-column-container">
            {/* --- 担当者 --- */}
            <div className="detail-item detail-item-editable" onDoubleClick={() => handleEditDoubleClick('owner_name')}>
              <span className="detail-label">担当者</span>
              {isEditing ? (
                <UserSelect
                  value={editData.owner_id}
                  onChange={(val) => setEditData({ ...editData, owner_id: val })}
                  placeholder="担当者を選択..."
                />
              ) : (
                <span className="detail-value">{meetingData.owner_name}</span>
              )}
            </div>

            {/* --- 活動日 --- */}
            <div className="detail-item">
              <span className="detail-label">活動日</span>
              <span className="detail-value">{meetingData.meeting_date}</span>
            </div>

            {/* --- 音声ファイル（編集中はCSSで非表示、アンマウントしない） --- */}
            <div style={{ display: isEditing ? 'none' : undefined }}>
              <FileUploadSection
                uploadUrlEndpoint={`/meetings/${meetingId}/audio/upload_url`}
                confirmEndpoint={`/meetings/${meetingId}/audio/confirm`}
                getEndpoint={`/meetings/${meetingId}/audio`}
                deleteEndpoint={`/meetings/${meetingId}/audio`}
                label="音声ファイル"
                accept={AUDIO_ACCEPT}
                maxFileSizeMB={300}
                onUploadComplete={async (results) => {
                  // 確定の応答に話者分離の必要なポイントが入っているため、確認モーダルではその値を使う
                  // (SCR-D-001 §5.2.1「取り直す時機」①。直後の取得が失敗していても値が残るように)
                  const uploaded = Array.isArray(results) ? results[results.length - 1] : null;
                  if (uploaded) diar.applyAudioAttachment(uploaded);
                  // 確定でサーバが話者分離の記録を消す・役割の判定からのやり直しを取り消すため、状態を取り直し、
                  // その応答（成否を問わない）を受け取ってから確認モーダルを開く
                  // (§5.2.1「取り直す時機」④・§6.2 図 6-1 の下の「手順 5 の後の取り直し」。#1646)
                  // 面談の取り直し（印）も並べて行う。確認モーダルの文は印に依らないため、待つのは表示を揃えるためだけ
                  await Promise.all([diar.reloadAfterAudioConfirmed(), reloadMeetingAfterAudioConfirmed()]);
                  setConfirmFromRestart(false);
                  setShowConfirmModal(true);
                }}
                onAttachmentChange={(att) => {
                  setAudioAttachment(att);
                  diar.applyAudioAttachment(att);
                }}
                replaceDisabledReason={isDiarizationPending ? AUDIO_REPLACE_BLOCKED_MESSAGE : null}
              />
              {/* 音声の取扱の明示 (DP-D-008)。話者分離の要素ではないため、フラグに依らず出す
                  (音声はフラグが無効でも同じく消える。PM の判断 #2332 comment 6583436568) */}
              <p className="diar-audio-notice" data-testid="diar-audio-notice">{AUDIO_HANDLING_NOTICE}</p>
            </div>

            {/* --- 作成日時 --- */}
            <div className="detail-item">
              <span className="detail-label">作成日時</span>
              <span className="detail-value">{formatToJST(meetingData.created_at)}</span>
            </div>
          </div>
        );

      // 'summary' タブ
      case 'summary':
        return (
          // フラグメント
          <React.Fragment>
            <div className="detail-item-full detail-item-editable" onDoubleClick={() => handleEditDoubleClick('meeting_summary')}>
              <div className="detail-label-row">
                <span className="detail-label">議事録要約</span>
                {!isEditing && <CopyButton text={meetingData.meeting_summary} />}
              </div>
              {showOutdatedNotice && <AIContentOutdatedNotice />}
              {isEditing ? (
                <textarea
                  className="detail-value-textarea" // 統一されたスタイル
                  rows="20" // rowsを20に調整（案件側とバランス調整）
                  value={editData.meeting_summary || ''}
                  onChange={(e) => handleEditChange(e, 'meeting_summary')}
                />
              ) : (
                <pre className="detail-value-pre">{meetingData.meeting_summary || '(要約データがありません)'}</pre>
              )}
            </div>

            {/* AI要約実行ボタン (編集モードじゃない時だけ表示) */}
            {!isEditing && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  className="modal-button-confirm"
                  onClick={handleClickGenerateSummary}
                  disabled={(!audioAttachment && !meetingData?.meeting_transcript) || processingStatus !== 'idle' || aiContentOutdated}
                  title={!audioAttachment && !meetingData?.meeting_transcript
                    ? 'AI要約の実行には音声ファイルまたは文字起こしが必要です'
                    : (aiContentOutdated ? AI_CONTENT_OUTDATED_DISABLED_REASON : '')}
                >
                  AI要約を実行
                </button>
              </div>
            )}
          </React.Fragment>
        );

      case 'transcript':
        return (
          <React.Fragment>
          <div className="detail-item-full detail-item-editable" onDoubleClick={() => handleEditDoubleClick('meeting_transcript')}>
            <div className="detail-label-row">
              <span className="detail-label">文字起こし</span>
              {!isEditing && <CopyButton text={meetingData.meeting_transcript} />}
            </div>
            {showOutdatedNotice && <AIContentOutdatedNotice />}
            {isEditing ? (
              <textarea
                className="detail-value-textarea"
                rows="20"
                value={editData.meeting_transcript || ''}
                onChange={(e) => handleEditChange(e, 'meeting_transcript')}
              />
            ) : diar.enabled ? (
              // 話者分離の結果の有無で切り替える (§6.1。本文のプレフィックスでは判定しない)
              <DiarizationPanel
                diarization={diar.diarization}
                canExecute={diar.canExecute}
                retryUnavailable={diar.retryUnavailable}
                onRetry={diar.diarization.execute}
              >
                <pre className="detail-value-pre">{meetingData.meeting_transcript || '(文字起こしデータがありません)'}</pre>
              </DiarizationPanel>
            ) : (
              <pre className="detail-value-pre">{meetingData.meeting_transcript || '(文字起こしデータがありません)'}</pre>
            )}
          </div>

          {/* 文字起こし実行ボタン (BTN-D-004)。条件を満たさないときは出さない (押せない状態で出さない) */}
          {canRestartTranscript && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button className="modal-button-confirm" onClick={openConfirmForRestart}>
                文字起こしを実行
              </button>
            </div>
          )}
          </React.Fragment>
        );

      case 'feedback':
        return (
          <React.Fragment>
            <div className="detail-item-full">
              <div className="detail-label-row">
                <span className="detail-label">AIフィードバック</span>
                <CopyButton text={meetingData.ai_feedback} />
              </div>
              {showOutdatedNotice && <AIContentOutdatedNotice />}
              <pre className="detail-value-pre">{meetingData.ai_feedback || '(フィードバックデータがありません)'}</pre>
            </div>

            {/* AIフィードバック実行ボタン (編集モードじゃない時だけ表示) */}
            {!isEditing && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  className="modal-button-confirm" // 既存のスタイルを流用
                  onClick={handleClickGenerateFeedback}
                  disabled={(!audioAttachment && !meetingData?.meeting_transcript) || processingStatus !== 'idle' || aiContentOutdated}
                  title={!audioAttachment && !meetingData?.meeting_transcript
                    ? 'AIフィードバックの実行には音声ファイルまたは文字起こしが必要です'
                    : (aiContentOutdated ? AI_CONTENT_OUTDATED_DISABLED_REASON : '')}
                >
                  AIフィードバックを実行
                </button>
              </div>
            )}
          </React.Fragment>
        );

      case 'memo':
        return (
          <div className="detail-item-full detail-item-editable" onDoubleClick={() => handleEditDoubleClick('memo')}>
            <div className="detail-label-row">
              <span className="detail-label">メモ</span>
              {!isEditing && <CopyButton text={meetingData.memo} />}
            </div>
            {isEditing ? (
              <>
                <textarea
                  className="detail-value-textarea"
                  rows="20"
                  maxLength={16000}
                  value={editData.memo || ''}
                  onChange={(e) => handleEditChange(e, 'memo')}
                  placeholder="メモを入力してください（最大16,000文字）"
                />
                <div style={{ textAlign: 'right', fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {(editData.memo || '').length} / 16,000 文字
                </div>
              </>
            ) : (
              <pre className="detail-value-pre">{meetingData.memo || '(メモがありません)'}</pre>
            )}
          </div>
        );

      default:
        return null;
    }
  };


  // --- -----------------
  // --- JSX (本体)
  // --- -----------------
  if (loading) return <SkeletonLoader variant="detail" rows={4} />;
  if (error) return <p className="error-message">エラー: {error}</p>;
  if (!meetingData) return null;

  return (
    <div>
      {/* アップロード中のローディングポップアップ */}
      {processingStatus !== 'idle' && (
        <div className="loading-popup-overlay">
          <div className="loading-popup-content">
            <div className="spinner"></div>
            <span>{STATUS_MESSAGES[processingStatus]}</span>
          </div>
        </div>
      )}

      {/* AI処理 確認モーダル */}
      <ConfirmModal
        isOpen={showConfirmModal}
        title="AI処理の確認"
        onConfirm={() => {
          // 話者分離を含めるかは実行を押した時点で決め、既存の AI 処理が始まるときに受け付けさせる (§6.2)
          diar.decideInclude();
          handleConfirmAIProcessing();
        }}
        onCancel={() => {
          diar.cancelInclude();
          handleCancelAIProcessing();
        }}
        disabled={processingStatus !== 'idle'}
      >
        <p style={{ margin: '0 0 10px 0' }}>
          {confirmFromRestart ? '音声ファイルはアップロード済みです。' : '音声ファイルのアップロードが完了しました。'}
        </p>
        <p>続けて、AIによる文字起こし・要約・フィードバックを生成しますか？</p>
        {/* 作成済みの内容を上書きすることを知らせる (§6.2)。音声を差し替えた後は、本文も含めて上書きし、
            手で編集した内容も残らない（丹羽様のご判断。#1952 comment 6569382996） */}
        {showOverwriteNotice ? (
          <p>{AI_CONTENT_OVERWRITE_NOTICE}</p>
        ) : confirmFromRestart && hasGeneratedAnalysis && (
          <p>作成済みのAI要約・AIフィードバックは作り直されます。</p>
        )}
        <DiarizationConfirmNotice
          enabled={diar.enabled}
          quota={diar.quota}
          audioRequired={diar.audioRequired}
          diarization={diar.diarizationForInclude}
        />
      </ConfirmModal>

      {/* 要約上書き 確認モーダル */}
      <ConfirmModal
        isOpen={showSummaryConfirmModal}
        title="AI要約の確認"
        onConfirm={handleConfirmOverwriteSummary}
        onCancel={handleCancelOverwriteSummary}
      >
        既に要約が存在します。<br />
        AI要約を再実行すると、現在の内容は上書きされますが、実行しますか？
      </ConfirmModal>

      {/* フィードバック上書き 確認モーダル */}
      <ConfirmModal
        isOpen={showFeedbackConfirmModal}
        title="AIフィードバックの確認"
        onConfirm={handleConfirmOverwriteFeedback}
        onCancel={handleCancelOverwriteFeedback}
      >
        既にフィードバックが存在します。<br />
        AIフィードバックを再実行すると、現在の内容は上書きされますが、実行しますか？
      </ConfirmModal>

      {/* AI処理エラーモーダル */}
      <ConfirmModal
        isOpen={processingError && processingStatus === 'idle'}
        title="エラー"
        onConfirm={() => setProcessingError(null)}
        confirmText="閉じる"
      >
        <p style={{ color: '#d32f2f' }}>{processingError}</p>
      </ConfirmModal>

      {/* ページタイトル */}
      {/* ページタイトルと戻るボタン */}
      <div className="detail-header">
        <div>
          <h1 className="page-title">活動詳細</h1>
          {meetingData?.opportunity_applicant_name && (
            <p className="breadcrumb-applicant">
              求職者：
              <a
                href={`/opportunity/${meetingData.opportunity_id}`}
                onClick={(e) => { e.preventDefault(); navigate(`/opportunity/${meetingData.opportunity_id}`); }}
              >
                {meetingData.opportunity_applicant_name}
              </a>
            </p>
          )}
        </div>
        <div className="header-actions">
          {!isEditing && (
            <>
              {/* 推薦文作成ボタン */}
              {meetingData?.opportunity_id && (
                <button
                  className="btn-recommendation"
                  onClick={() => navigate(`/opportunity/${meetingData.opportunity_id}?tab=proposed_jobs`)}
                  style={{ marginRight: '12px' }}
                >
                  <span className="icon">📝</span>
                  推薦文を作成
                </button>
              )}
              <button className="btn-delete" onClick={() => setShowDeleteConfirm(true)} style={{ marginRight: '10px' }}>
                削除
              </button>
              <button className="modal-button-cancel" onClick={handleBackClick}>
                前ページに戻る
              </button>
            </>
          )}
        </div>
      </div>

      {/* 削除確認モーダル */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="削除の確認"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmText={editLoading ? '処理中...' : '削除する'}
        cancelText="キャンセル"
        confirmClassName="btn-delete"
        disabled={editLoading}
      >
        <p style={{ textAlign: 'center' }}>この活動を削除してもよろしいですか？</p>
      </ConfirmModal>

      <div className="detail-card">

        {/* 活動名 (編集モード対応) */}
        <div className="detail-item detail-item-editable" onDoubleClick={() => handleEditDoubleClick('name')}>
          <span className="detail-label">活動名</span>
          {isEditing ? (
            <input
              type="text"
              className="card-title-input"
              value={editData.name}
              onChange={(e) => handleEditChange(e, 'name')}
              autoFocus
            />
          ) : (
            <span className="detail-value-large">{meetingData.name}</span>
          )}
        </div>

        {/* 話者分離のエラーを読み上げでも知らせる領域 (§7.2・§6.3 ③)。どのタブを開いていても描画しておき、
            この画面を開いている間に起きたエラーがあり、タブの中のエラーの文（role="alert"）が描かれていないときだけ
            文を入れる（読み上げは、既にある領域の中身が変わったときに起きるため、印と一緒に領域ごと足さない。
            タブの中の文が描かれていれば、そちらが読み上げるため二重にしない。#1643）。空にするときは何も知らせない */}
        {diar.enabled && (
          <div className="diar-visually-hidden" aria-live="polite" data-testid="diar-live-region">
            {announceDiarizationError ? ERROR_LIVE_MESSAGE : ''}
          </div>
        )}

        {/* タブナビゲーション */}
        <div className="tab-nav">
          <button
            className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveTab('basic')}
            disabled={isEditing || processingStatus !== 'idle'}
          >
            基本情報
          </button>
          <button
            className={`tab-button ${activeTab === 'memo' ? 'active' : ''}`}
            onClick={() => setActiveTab('memo')}
            disabled={isEditing || processingStatus !== 'idle'}
          >
            メモ
          </button>
          <button
            className={`tab-button ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveTab('summary')}
            disabled={isEditing || processingStatus !== 'idle'}
          >
            要約
          </button>
          <button
            className={`tab-button ${activeTab === 'transcript' ? 'active' : ''}`}
            onClick={() => setActiveTab('transcript')}
            disabled={isEditing || processingStatus !== 'idle'}
          >
            文字起こし
            {/* 話者分離の処理中の印。他のタブを見ていても処理中と分かるように (§6.4「タブの外での表示」) */}
            {isDiarizationPending && (
              <span className="diar-tab-mark" data-testid="diar-tab-mark" data-state="pending">
                <span className="spinner" aria-hidden="true" />
                <span className="diar-visually-hidden">{TAB_MARK_PENDING_LABEL}</span>
              </span>
            )}
            {/* 話者分離のエラーの印。他のタブを見ていても失敗に気づけるように (§6.3・§6.4。#1468・#1643) */}
            {hasDiarizationError && (
              <span className="diar-tab-mark diar-tab-mark--error" data-testid="diar-tab-mark" data-state="error">
                <SpeakerIcon name="triangle-alert" />
                <span className="diar-visually-hidden">{TAB_MARK_ERROR_LABEL}</span>
              </span>
            )}
          </button>
          <button
            className={`tab-button ${activeTab === 'feedback' ? 'active' : ''}`}
            onClick={() => setActiveTab('feedback')}
            disabled={isEditing || processingStatus !== 'idle'}
          >
            AIフィードバック
          </button>
        </div>

        {/* タブコンテンツ */}
        <div className="tab-content">
          {renderTabContent()}
        </div>

      </div>

      {/* 編集モード時のフッター (保存/キャンセル) */}
      {isEditing && (
        <div className="edit-actions-footer">
          {editError && (
            <p className="modal-error-message">{editError}</p>
          )}
          <div className="edit-actions-buttons">
            <button
              className="modal-button-cancel"
              onClick={handleCancelEdit}
              disabled={editLoading}
            >
              キャンセル
            </button>
            <button
              className="modal-button-confirm"
              onClick={handleSaveEdit}
              disabled={editLoading}
            >
              {editLoading ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      )}

      {/* 💡 プロンプト選択モーダル */}
      <PromptSelectModal
        key={promptSelectContext || 'prompt-modal'}
        isOpen={showPromptSelectModal}
        onClose={() => {
          diar.cancelInclude();
          handleClosePromptSelectModal();
        }}
        onConfirm={(promptId) => {
          // アップロード後の AI 処理は、2 つ目のプロンプト（FB）の確定で始まる。そのときに話者分離も受け付けさせる
          // （手前でキャンセルされたら話者分離も実行しない）
          if (promptSelectContext === 'upload_feedback') diar.startIfIncluded();
          handlePromptSelectConfirm(promptId);
        }}
        promptType={PROMPT_MODAL_CONFIG[promptSelectContext]?.type || 'summary'}
        title={PROMPT_MODAL_CONFIG[promptSelectContext]?.title || 'AI要約を実行'}
        message="使用するプロンプトを選択してください"
        confirmButtonText={PROMPT_MODAL_CONFIG[promptSelectContext]?.confirmBtn || '実行'}
        cancelButtonText="キャンセル"
      />
    </div>
  );
}

/**
 * 面談の ID を `key` にして、ID が変わるたびに画面を作り直す (SCR-D-001 §6.1「同じ画面のまま別の面談に移ったとき」。#1693)。
 *
 * 経路 `/activity/:meetingId` は、ブラウザの履歴から間の画面を飛ばして移ると、部品を作り直さずに ID だけを変える。
 * 作り直さないと、前の面談の話者分離の状態・受付の失敗・編集中の入力・処理中の応答などが、いまの面談の画面に残る
 * （編集中の入力は、保存を押すといまの面談に書き込まれる）。値ごとに ID を確かめる形にしないのは、
 * これから足す値にも同じ手当てを思い出す必要が生じるため。
 * `App.js` の経路ではなくここで付ける: 単体テストはこの部品を直接描くため、ここに無いとテストが作り直しを通らない
 */
function ActivityDetailPage() {
  const { meetingId } = useParams();
  return <ActivityDetailContent key={meetingId} meetingId={meetingId} />;
}

export default ActivityDetailPage;